/**
 * seo.js — Helpers SEO pour SPA (gestion <head> dynamique par route).
 *
 * Problème résolu : en SPA, toutes les routes partagent le HTML statique de
 * public/index.html → même <title>, même canonical, même description pour
 * Google tant que le JS n'a pas tourné. Pire : le canonical pointe vers "/"
 * partout, ce qui dit à Google « cette page est un doublon de l'accueil ».
 *
 * Ces helpers, appelés dans le useEffect de chaque page, corrigent le <head>
 * côté client. Googlebot exécute le JS et lit la version corrigée.
 * (Le pré-rendu au build reste le complément idéal — voir react-snap.)
 */

const ORIGIN = 'https://www.finanalyse.fr';

/** Met à jour (ou crée) une balise <link rel="canonical">. */
export function setCanonical(path = '/') {
  if (typeof document === 'undefined') return;
  const href = path.startsWith('http') ? path : `${ORIGIN}${path}`;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);

  // og:url suit le canonical (cohérence partages sociaux)
  let ogUrl = document.querySelector('meta[property="og:url"]');
  if (!ogUrl) {
    ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    document.head.appendChild(ogUrl);
  }
  ogUrl.setAttribute('content', href);
}

/** Met à jour une balise <meta name="..."> (description, keywords, robots…). */
export function setMetaName(name, content) {
  if (typeof document === 'undefined' || content == null) return;
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

/** Met à jour une balise <meta property="..."> (og:title, og:description…). */
export function setMetaProperty(property, content) {
  if (typeof document === 'undefined' || content == null) return;
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

/**
 * Applique l'ensemble des balises SEO d'une page en un appel.
 * @param {Object} opts
 * @param {string} opts.title        - <title> + og:title + twitter:title
 * @param {string} opts.description  - meta description + og/twitter description
 * @param {string} opts.path         - chemin canonique (ex: "/valorisation")
 * @param {string} [opts.keywords]   - meta keywords (optionnel)
 * @param {boolean}[opts.scrollTop]  - remonter en haut (défaut true)
 */
/**
 * Injecte (ou remplace) un bloc JSON-LD PROPRE À LA PAGE courante.
 *
 * Pourquoi (audit 2026-08-15) : public/index.html portait 9 blocs JSON-LD que le
 * pré-rendu recopiait à l'identique sur les 82 pages — dont une FAQPage de 11
 * questions et un HowTo de 5 étapes présents jusque sur /blog et /tarifs, qui ne
 * les affichent pas. Google demande que le balisage décrive le contenu VISIBLE de
 * la page ; un décalage coûte l'éligibilité aux résultats enrichis, voire une
 * action manuelle « Structured data markup mismatch ».
 *
 * Chaque bloc porte un id stable : re-rendre une page remplace son bloc au lieu
 * d'en empiler un second. Le nettoyage est fait au montage de la page.
 *
 * @param {string} id   - identifiant stable, ex. 'jsonld-home-faq'
 * @param {Object} data - objet JSON-LD (sans @context, ajouté ici)
 */
export function setPageJsonLd(id, data) {
  if (typeof document === 'undefined' || !data) return;
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.setAttribute('data-page-jsonld', '');
  script.textContent = JSON.stringify({ '@context': 'https://schema.org', ...data });
  document.head.appendChild(script);
}

/**
 * Retire tous les blocs JSON-LD de page posés par setPageJsonLd.
 * À appeler au montage d'une page avant d'injecter les siens, pour qu'une
 * navigation client (SPA) ne laisse pas traîner le balisage de la page quittée.
 */
export function clearPageJsonLd() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('script[data-page-jsonld]').forEach((s) => s.remove());
}

export function setSEO({ title, description, path, keywords, scrollTop = true }) {
  if (typeof document === 'undefined') return;
  if (title) {
    document.title = title;
    setMetaProperty('og:title', title);
    setMetaName('twitter:title', title);
  }
  if (description) {
    setMetaName('description', description);
    setMetaProperty('og:description', description);
    setMetaName('twitter:description', description);
  }
  if (keywords) setMetaName('keywords', keywords);
  if (path) setCanonical(path);
  if (scrollTop) window.scrollTo(0, 0);
}
